import React, {Fragment, useEffect, useMemo, useRef, useState} from 'react';
import {
	Autocomplete,
	Button,
	Divider,
	IconButton,
	Skeleton,
	Stack,
	Typography,
	useTheme,
} from '@mui/material';
import {format} from 'date-fns';
import {ptBR} from 'date-fns/locale';
import {QueryParamsEnum, FeaturesEnum} from 'presentation/constants';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import Icon from 'presentation/icons';
import {useMediaQueries, useQueryParams} from 'presentation/pages';
import {getCurrentYear} from 'presentation/utils/getCurrentYear';
import {formatText} from 'presentation/utils';
import {useLocation} from 'react-router-dom';
import ModalDatePicker from '../ModalDatePicker';
import {MonthYearPickerCalendar} from '../MonthYearPickerCalendar';
import {
	StyledBadge,
	StyledBoxItem,
	StyledButton,
	StyledButtonLetter,
	StyledContentButton,
	StyledFilterInputBase,
	StyledFilterRoot,
	StyledFilterSearch,
	StyledTextField,
} from './styles';
import {
	AutocompleteProps,
	FilterButtonProps,
	FilterByLetterProps,
	FilterDateMonthYearProps,
	FilterRootProps,
	FilterSearchProps,
	OptionType,
} from './types';
import {ModalDatePickerProps} from '../ModalDatePicker/types';
import {useIsAutocompleteFocusedStore} from './store/useIsAutocompleteFocusedStore';
import {useCurrentValueInputTypingStore} from './store/useCurrentValueInputTypingStore';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {useServicePermission} from 'presentation/hooks/useServicePermission';
import {ServicesTypesEnum} from 'presentation/pages/private/services/types';

function FilterRoot({
	children,
	$disableShadow,
	$customBackgroundColor,
	...cardProps
}: FilterRootProps) {
	return (
		<StyledFilterRoot
			$disableShadow={$disableShadow}
			$customBackgroundColor={$customBackgroundColor}
			{...cardProps}>
			{children}
		</StyledFilterRoot>
	);
}

function FilterSearch({
	placeholder,
	readOnly,
	value,
	handleChange,
	clearSearch,
	dataTagId,
}: FilterSearchProps) {
	const {
		SEARCH_PARAMS: {query},
		setSearchParams,
	} = useQueryParams();
	const theme = useTheme();
	const {state} = useLocation();

	const [currentValue, setCurrentValue] = useState<string | null | undefined>(query ?? value);

	function handleChangeSearchParams(newValue: string) {
		setSearchParams(
			prev => {
				prev.set(QueryParamsEnum.SEARCH_QUERY, newValue);
				prev.delete(QueryParamsEnum.SEARCH_BY_LETTER);

				return prev;
			},
			{
				replace: true,
			},
		);
	}

	function clearSearchParams() {
		setSearchParams(
			prev => {
				prev.delete(QueryParamsEnum.SEARCH_QUERY);
				prev.delete(QueryParamsEnum.SEARCH_BY_LETTER);

				return prev;
			},
			{
				replace: true,
				state,
			},
		);
	}

	const handleInputChange = (newValue: string) => {
		setCurrentValue(newValue);
		if (!newValue) {
			setCurrentValue('');
			clearSearchParams();
			return;
		}

		handleChangeSearchParams(newValue);
		if (handleChange) {
			handleChange(newValue);
		}
	};

	const handleInputClear = () => {
		setCurrentValue('');
		clearSearchParams();
		if (clearSearch) {
			clearSearch();
		}
	};

	useEffect(() => {
		if (currentValue) {
			handleChangeSearchParams(currentValue);
		}
	}, [currentValue]);

	useEffect(() => {
		if (!query || query === 'clear') {
			handleInputClear();
		}
	}, [query]);

	return (
		<StyledFilterSearch
			component="form"
			elevation={0}
			onSubmit={event => event.preventDefault()}>
			<Icon name="search-lens" color={theme.colors.gray['54Percent']} />
			<StyledFilterInputBase
				data-tag-id={dataTagId}
				readOnly={readOnly}
				value={currentValue}
				onChange={e => handleInputChange(e.target.value)}
				placeholder={placeholder}
				inputProps={{
					'aria-label': placeholder,
					style: {cursor: readOnly ? 'pointer' : 'initial'},
				}}
			/>
			{currentValue ? (
				<IconButton
					type="button"
					aria-label="clear search"
					size="small"
					onClick={handleInputClear}>
					<Icon name="close-filled" color={theme.colors.gray['54Percent']} />
				</IconButton>
			) : null}
		</StyledFilterSearch>
	);
}

function FilterSearchWithAutoComplete<T>({
	placeholder,
	value = '',
	handleChange,
	clearSearch,
	autocompleteConfig,
	loadingList,
}: AutocompleteProps<T>) {
	const {
		SEARCH_PARAMS: {query},
		setSearchParams,
	} = useQueryParams();
	const theme = useTheme();
	const {isMobile} = useMediaQueries();

	const [currentValue, setCurrentValue] = useState<string>(query ?? value);
	const [isAutocompleteFocusedState, setAutocompleteFocused] = useState(false);
	const globalAutocompleteFocus = useIsAutocompleteFocusedStore(
		state => state.isAutocompleteFocused,
	);
	const setGlobalAutocompleteFocus = useIsAutocompleteFocusedStore(
		state => state.setIsAutocompleteFocused,
	);
	const setTypingValue = useCurrentValueInputTypingStore(
		state => state.setCurrentValueInputTyping,
	);

	const updateSearchParams = (newValue: string, autocompleteQueryParam?: string) => {
		setSearchParams(
			prev => {
				prev.set(QueryParamsEnum.SEARCH_QUERY, newValue);
				prev.delete(QueryParamsEnum.SEARCH_BY_LETTER);
				if (autocompleteQueryParam)
					prev.set(QueryParamsEnum.AUTOCOMPLETE_QUERY, autocompleteQueryParam);
				return prev;
			},
			{replace: true},
		);
	};

	const clearSearchParams = () => {
		setSearchParams(
			prev => {
				prev.delete(QueryParamsEnum.SEARCH_QUERY);
				prev.delete(QueryParamsEnum.AUTOCOMPLETE_QUERY);
				prev.delete(QueryParamsEnum.SEARCH_BY_LETTER);
				return prev;
			},
			{replace: true},
		);
	};

	const handleInputChange = (newValue: OptionType | null) => {
		const newLabel = newValue?.label || '';
		setCurrentValue(newLabel);
		if (newLabel) {
			updateSearchParams(newLabel, String(newValue?.id));
			handleChange?.(newLabel);
			setAutocompleteFocused(false);
		}
		setGlobalAutocompleteFocus(false);
	};

	const handleInputClear = () => {
		setCurrentValue('');
		clearSearchParams();
		setAutocompleteFocused(false);
		setGlobalAutocompleteFocus(false);
		setTypingValue('');
		clearSearch?.();
	};

	useEffect(() => {
		if (query && query !== 'clear') {
			setCurrentValue(query);
		} else {
			handleInputClear();
		}
	}, [query]);

	return (
		<Autocomplete
			id="select-provider"
			sx={{width: '100%'}}
			options={(autocompleteConfig?.options ?? [])
				.map(option => ({
					id: (option as OptionType).id,
					label: (option as OptionType).label,
				}))
				.sort((current, next) => current.label.localeCompare(next.label))}
			value={{id: undefined, label: currentValue}}
			onChange={(_, newValue) => handleInputChange(newValue as OptionType | null)}
			freeSolo
			onBlur={() => setAutocompleteFocused(false)}
			getOptionLabel={option =>
				typeof option === 'object' && option !== null ? formatText(option.label) : ''
			}
			getOptionDisabled={() => !!loadingList}
			open={isAutocompleteFocusedState && !isMobile}
			loadingText={false}
			loading={loadingList}
			renderOption={(props, option) => {
				const {key, ...rest} = props;

				return (
					<li key={key} {...rest}>
						<StyledBoxItem>
							{loadingList ? (
								<Skeleton variant="text" sx={{width: '100%'}} />
							) : (
								<Fragment>
									<Typography>{formatText(option.label)}</Typography>
									<Icon
										name="arrow-right-filled"
										color={theme.brand.primary.blue}
									/>
								</Fragment>
							)}
						</StyledBoxItem>
					</li>
				);
			}}
			renderInput={params => (
				<StyledTextField
					{...params}
					placeholder={placeholder}
					focused={isAutocompleteFocusedState || globalAutocompleteFocus}
					InputProps={{
						...params.InputProps,
						onChange: e => {
							setTypingValue(e.target.value);
							setCurrentValue(e.target.value);
							setAutocompleteFocused(true);
						},
						onClick: () => {
							if (isMobile) {
								setGlobalAutocompleteFocus(true);
							} else {
								setAutocompleteFocused(true);
							}
						},
						startAdornment: (
							<Fragment>
								<Icon name="search-lens" color={theme.colors.gray['54Percent']} />
								{params.InputProps.startAdornment}
							</Fragment>
						),
						endAdornment: currentValue ? (
							<IconButton
								type="button"
								aria-label="clear search"
								size="small"
								onClick={event => {
									event.stopPropagation();
									handleInputClear();
								}}>
								<Icon name="close-filled" color={theme.colors.gray['54Percent']} />
							</IconButton>
						) : null,
					}}
				/>
			)}
		/>
	);
}

function FilterDate({...modalProps}: ModalDatePickerProps) {
	return <ModalDatePicker showClearButton {...modalProps} />;
}

function FilterDateMonthYear({
	maxDate,
	minDate,
	viewMode,
	customButtonLabel,
	shouldMaxDateBeDefault,
	...buttonProps
}: FilterDateMonthYearProps) {
	const {SEARCH_PARAMS} = useQueryParams();
	const {month, year} = SEARCH_PARAMS;
	const [openState, setOpenState] = useState(false);
	let currentDateNow = format(new Date(), 'MM/yyyy', {locale: ptBR});
	const headerTitle = viewMode === 'year' ? 'Selecione o ano' : 'Selecione o mês e ano';

	const currentYear = getCurrentYear({year, maxDate, minDate});

	if (year) {
		currentDateNow = `Ano de ${currentYear}`;
	}
	if (!!month && !!year) {
		currentDateNow = `${month}/${year}`;
	}
	const UNSELECTED_VALUES = !month && !year;
	const SHOW_CUSTOM_BUTTON = UNSELECTED_VALUES && customButtonLabel;
	const TITLE_BUTTON = SHOW_CUSTOM_BUTTON ? customButtonLabel : currentDateNow;

	const handleOpen = () => {
		setOpenState(true);
	};

	return (
		<Stack direction="row" justifyContent={'start'}>
			<Button
				variant="text"
				onClick={handleOpen}
				endIcon={<Icon name="edit" size={20} />}
				{...buttonProps}>
				{TITLE_BUTTON}
			</Button>
			<MonthYearPickerCalendar
				open={openState}
				setOpen={setOpenState}
				headerTitle={headerTitle}
				viewMode={viewMode}
				maxDate={maxDate}
				minDate={minDate}
				shouldMaxDateBeDefault={shouldMaxDateBeDefault}
			/>
		</Stack>
	);
}

function FilterButton({
	children = 'Filtrar',
	count,
	customLabel,
	...buttonProps
}: FilterButtonProps) {
	return (
		<StyledButton {...buttonProps}>
			<StyledBadge badgeContent={count} invisible={!count} color="error">
				<StyledContentButton>{customLabel ?? children}</StyledContentButton>
			</StyledBadge>
		</StyledButton>
	);
}

function getFirstLetterOfSpecialty(name: string) {
	return name?.charAt(0).toUpperCase() ?? '';
}

function FilterByLetter({filteredList, dataTagId}: FilterByLetterProps) {
	const {pathname} = useLocation();
	const {serviceTypeByPath} = useServicePermission();
	const filterLetters = useMemo(() => filteredList.map(item => item.category), [filteredList]);
	const {SEARCH_PARAMS, setSearchParams, clearSearch} = useQueryParams();
	const {searchByLetter, specialtyName} = SEARCH_PARAMS;
	const isBookingByMedicalGuideFlow = useBookingStore(
		state => state.bookingData.isBookingByMedicalGuideFlow,
	);
	const {isEnabled: isSchedulingByMedicalGuideEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_SCHEDULING_BY_MEDICAL_GUIDE,
	);
	const lastAutoSetSpecialtyRef = useRef<string | null>(null);

	function handleClick(letter: string) {
		if (searchByLetter === letter) {
			clearSearch();
			return;
		}

		setSearchParams(prev => {
			prev.set(QueryParamsEnum.SEARCH_BY_LETTER, letter);
			return prev;
		});
	}

	const currentServiceType = serviceTypeByPath(pathname);

	useEffect(() => {
		if (
			!specialtyName ||
			!isSchedulingByMedicalGuideEnabled ||
			!isBookingByMedicalGuideFlow ||
			lastAutoSetSpecialtyRef.current === specialtyName ||
			currentServiceType === ServicesTypesEnum.EXAMS
		) {
			return;
		}

		lastAutoSetSpecialtyRef.current = specialtyName;

		const firstLetter = getFirstLetterOfSpecialty(specialtyName);

		setSearchParams(prev => {
			prev.set(QueryParamsEnum.SEARCH_BY_LETTER, firstLetter);
			return prev;
		});
	}, [
		specialtyName,
		isSchedulingByMedicalGuideEnabled,
		setSearchParams,
		isBookingByMedicalGuideFlow,
	]);

	return (
		<Stack flexDirection="row" overflow="auto">
			{filterLetters.map(letter => (
				<Fragment key={letter}>
					<StyledButtonLetter
						onClick={() => handleClick(letter)}
						variant={'soft'}
						$active={searchByLetter === letter}
						data-tag-id={dataTagId ? `${dataTagId}${letter}` : undefined}>
						{letter}
					</StyledButtonLetter>
					<Divider orientation="vertical" variant="middle" flexItem />
				</Fragment>
			))}
		</Stack>
	);
}

export const Filter = {
	Root: FilterRoot,
	Search: FilterSearch,
	Date: FilterDate,
	MonthYear: FilterDateMonthYear,
	Button: FilterButton,
	Letter: FilterByLetter,
	SearchWithAutocomplete: FilterSearchWithAutoComplete,
};
